import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Container,
  Title,
  Text,
  Paper,
  Group,
  Badge,
  Stack,
  Loader,
  Center,
  Box,
  Accordion,
  TextInput,
  Textarea,
  Select,
  Button,
  Modal,
  Divider,
  ScrollArea,
  ActionIcon,
  Tooltip,
  Grid,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconFileText,
  IconMessage,
  IconEdit,
  IconCheck,
  IconX,
  IconClipboard,
  IconUser,
  IconMicrophone,
  IconDownload,
  IconFileTypePdf,
  IconFileTypeDocx,
} from '@tabler/icons-react';
import { conversationsApi, type ConversationMessage } from '@/services/api.service';
import { fillingApi } from '@/services/templates.api';
import type { DocumentFilling, FilledField } from '@/types/template.types';

export const MeetingDetails = () => {
  const { roomCode } = useParams<{ roomCode: string }>();
  const navigate = useNavigate();

  const [filling, setFilling] = useState<DocumentFilling | null>(null);
  const [conversation, setConversation] = useState<ConversationMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit modal state
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [editingField, setEditingField] = useState<FilledField | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Document generation state
  const [generatingDoc, setGeneratingDoc] = useState<'pdf' | 'docx' | null>(null);

  // Load data
  const loadData = useCallback(async (showLoading = true) => {
    if (!roomCode) return;

    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const [fillingData, conversationResponse] = await Promise.all([
        fillingApi.getByRoomCode(roomCode),
        conversationsApi.getHistory(roomCode),
      ]);

      setFilling(fillingData);
      setConversation(conversationResponse.messages);
    } catch (err: any) {
      console.error('Error loading meeting details:', err);
      setError(err.response?.data?.detail || 'Erro ao carregar detalhes da reunião');
    } finally {
      setLoading(false);
    }
  }, [roomCode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle edit field
  const handleEditField = (field: FilledField) => {
    setEditingField(field);
    setEditValue(field.value || '');
    openEditModal();
  };

  // Save edited field
  const handleSaveField = async () => {
    if (!editingField || !roomCode) return;

    try {
      setSaving(true);
      await fillingApi.fillField(roomCode, editingField.fieldId, { value: editValue });

      notifications.show({
        title: 'Campo atualizado',
        message: `${editingField.displayName} foi atualizado com sucesso`,
        color: 'green',
      });

      closeEditModal();
      loadData(false); // Reload sem mostrar loading
    } catch (err: any) {
      console.error('Error saving field:', err);
      notifications.show({
        title: 'Erro',
        message: err.response?.data?.detail || 'Erro ao salvar campo',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  // Clear field
  const handleClearField = async () => {
    if (!editingField || !roomCode) return;

    try {
      setSaving(true);
      await fillingApi.clearField(roomCode, editingField.fieldId);

      notifications.show({
        title: 'Campo limpo',
        message: `${editingField.displayName} foi limpo`,
        color: 'blue',
      });

      closeEditModal();
      loadData(false); // Reload sem mostrar loading
    } catch (err: any) {
      console.error('Error clearing field:', err);
      notifications.show({
        title: 'Erro',
        message: err.response?.data?.detail || 'Erro ao limpar campo',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  // Generate document
  const handleGenerateDocument = async (format: 'pdf' | 'docx') => {
    if (!roomCode) return;

    try {
      setGeneratingDoc(format);

      const { blob, filename } = await fillingApi.generateDocument(roomCode, format, true);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      notifications.show({
        title: 'Documento gerado',
        message: `O documento ${format.toUpperCase()} foi baixado com sucesso`,
        color: 'green',
      });
    } catch (err: any) {
      console.error('Error generating document:', err);
      notifications.show({
        title: 'Erro',
        message: err.response?.data?.detail || 'Erro ao gerar documento',
        color: 'red',
      });
    } finally {
      setGeneratingDoc(null);
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Group fields by groupName
  const groupedFields = filling?.fields.reduce((acc, field) => {
    const group = field.groupName || 'Geral';
    if (!acc[group]) acc[group] = [];
    acc[group].push(field);
    return acc;
  }, {} as Record<string, FilledField[]>) || {};

  // Render field value input in edit modal
  const renderFieldInput = () => {
    if (!editingField) return null;

    if (editingField.fieldType === 'enum' && editingField.enumValues) {
      return (
        <Select
          label={editingField.displayName}
          description={editingField.description}
          value={editValue}
          onChange={(val) => setEditValue(val || '')}
          data={editingField.enumValues.map((v) => ({ value: v, label: v }))}
          placeholder="Selecione uma opção"
          clearable
        />
      );
    }

    if (editingField.fieldType === 'textarea') {
      return (
        <Textarea
          label={editingField.displayName}
          description={editingField.description}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          minRows={4}
          autosize
          maxRows={10}
        />
      );
    }

    return (
      <TextInput
        label={editingField.displayName}
        description={editingField.description}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        type={editingField.fieldType === 'number' ? 'number' : 'text'}
      />
    );
  };

  if (loading) {
    return (
      <Center style={{ height: '60vh' }}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Carregando detalhes da reunião...</Text>
        </Stack>
      </Center>
    );
  }

  if (error || !filling) {
    return (
      <Center style={{ height: '60vh' }}>
        <Stack align="center" gap="md">
          <Text size="xl" fw={600} c="red">
            {error || 'Reunião não encontrada'}
          </Text>
          <Button
            leftSection={<IconArrowLeft size={16} />}
            variant="light"
            onClick={() => navigate('/dashboard')}
          >
            Voltar ao Dashboard
          </Button>
        </Stack>
      </Center>
    );
  }

  // Configuração de status consistente com MeetingsPage
  const getStatusConfig = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      in_progress: { color: 'blue', label: 'Em Andamento' },
      completed: { color: 'green', label: 'Concluída' },
      abandoned: { color: 'gray', label: 'Abandonada' },
    };
    return config[status] || config.in_progress;
  };

  const statusConfig = getStatusConfig(filling.status);

  return (
    <Container size="lg" py="xl">
      {/* Header */}
      <Paper p="md" mb="lg" withBorder>
        <Group justify="space-between" align="flex-start">
          {/* Lado esquerdo - Informações do documento */}
          <Group align="flex-start">
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={() => navigate('/meetings')}
            >
              <IconArrowLeft size={20} />
            </ActionIcon>

            <Box>
              {/* Linha 1: Título + Versão */}
              <Group gap="sm" align="center">
                <Title order={2}>{filling.templateTitle}</Title>
                <Badge color="teal" variant="light">
                  v{filling.templateVersion}
                </Badge>
              </Group>

              {/* Linha 2: Todos os badges de status na mesma linha */}
              <Group gap="xs" mt="xs">
                <Badge
                  color={statusConfig.color}
                  variant="filled"
                  leftSection={filling.status === 'completed' ? <IconCheck size={14} /> : null}
                >
                  {statusConfig.label}
                </Badge>
                <Badge color="cyan" variant="light">
                  {filling.completionPercentage.toFixed(0)}% preenchido
                </Badge>
                <Badge color="gray" variant="light">
                  {formatDate(filling.createdAt)}
                </Badge>
                <Badge color="blue" variant="light">
                  Sala: {filling.roomCode}
                </Badge>
              </Group>
            </Box>
          </Group>

          {/* Lado direito - Botões empilhados verticalmente */}
          <Stack gap="xs">
            <Tooltip label="Baixar como PDF">
              <Button
                variant="light"
                color="red"
                leftSection={<IconFileTypePdf size={18} />}
                loading={generatingDoc === 'pdf'}
                disabled={generatingDoc !== null}
                onClick={() => handleGenerateDocument('pdf')}
                fullWidth
              >
                PDF
              </Button>
            </Tooltip>

            <Tooltip label="Baixar como Word">
              <Button
                variant="light"
                color="blue"
                leftSection={<IconFileTypeDocx size={18} />}
                loading={generatingDoc === 'docx'}
                disabled={generatingDoc !== null}
                onClick={() => handleGenerateDocument('docx')}
                fullWidth
              >
                DOCX
              </Button>
            </Tooltip>
          </Stack>
        </Group>
      </Paper>

      {/* Resumo da Reunião - Largura total */}
      <Paper p="md" mb="lg" withBorder>
        <Group gap="xs" mb="md">
          <IconClipboard size={20} />
          <Title order={4}>Resumo da Reunião</Title>
        </Group>

        {filling.summary ? (
          <Box className="markdown-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {filling.summary}
            </ReactMarkdown>
            {filling.endedAt && (
              <Text size="xs" c="dimmed" mt="md">
                Gerado em: {formatDate(filling.endedAt)}
              </Text>
            )}
          </Box>
        ) : (
          <Text c="dimmed" fs="italic">
            O resumo será gerado quando a conversa for encerrada.
          </Text>
        )}
      </Paper>

      {/* Seção inferior: duas colunas iguais */}
      <Grid gutter="lg">
        {/* Coluna esquerda: Campos preenchidos */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper p="md" withBorder style={{ height: '500px' }}>
            <Group gap="xs" mb="md">
              <IconFileText size={20} />
              <Title order={4}>Campos Preenchidos</Title>
              <Badge size="sm" variant="light">
                {filling.filledFields}/{filling.totalFields}
              </Badge>
            </Group>

            <ScrollArea h={420} offsetScrollbars>
              <Accordion variant="separated" radius="sm">
                {Object.entries(groupedFields).map(([groupName, fields]) => (
                  <Accordion.Item key={groupName} value={groupName}>
                    <Accordion.Control>
                      <Group gap="xs">
                        <Text fw={500}>{groupName}</Text>
                        <Badge size="xs" variant="light">
                          {fields.filter((f) => f.status === 'filled').length}/{fields.length}
                        </Badge>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="sm">
                        {fields.map((field) => (
                          <Paper
                            key={field.id}
                            p="sm"
                            withBorder
                            style={{
                              borderLeftWidth: 4,
                              borderLeftColor:
                                field.status === 'filled'
                                  ? 'var(--mantine-color-green-6)'
                                  : 'var(--mantine-color-red-6)',
                            }}
                          >
                            <Group justify="space-between" align="flex-start">
                              <Box style={{ flex: 1 }}>
                                <Group gap="xs">
                                  <Text size="sm" fw={500}>
                                    {field.displayName}
                                  </Text>
                                  {field.isRequired && (
                                    <Badge size="xs" color="red" variant="light">
                                      Obrigatório
                                    </Badge>
                                  )}
                                </Group>
                                {field.value ? (
                                  <Text size="sm" mt="xs" style={{ whiteSpace: 'pre-wrap' }}>
                                    {field.value}
                                  </Text>
                                ) : (
                                  <Text size="sm" c="dimmed" mt="xs" fs="italic">
                                    Campo não preenchido
                                  </Text>
                                )}
                              </Box>
                              <Tooltip label="Editar campo">
                                <ActionIcon
                                  variant="subtle"
                                  color="blue"
                                  onClick={() => handleEditField(field)}
                                >
                                  <IconEdit size={16} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Paper>
                        ))}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </ScrollArea>
          </Paper>
        </Grid.Col>

        {/* Coluna direita: Conversa completa */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper p="md" withBorder style={{ height: '500px' }}>
            <Group gap="xs" mb="md">
              <IconMessage size={20} />
              <Title order={4}>Conversa Completa</Title>
              <Badge size="sm" variant="light">
                {conversation.length} mensagens
              </Badge>
            </Group>

            <ScrollArea h={420} offsetScrollbars>
              {conversation.length > 0 ? (
                <Stack gap="xs">
                  {conversation.map((msg) => (
                    <Paper
                      key={msg.id}
                      p="xs"
                      bg={msg.messageType === 'transcription' ? 'blue.0' : 'gray.0'}
                      radius="sm"
                    >
                      <Group gap="xs" mb={4}>
                        {msg.messageType === 'transcription' ? (
                          <IconMicrophone size={14} color="var(--mantine-color-blue-6)" />
                        ) : (
                          <IconUser size={14} color="var(--mantine-color-gray-6)" />
                        )}
                        <Text size="xs" fw={500}>
                          {msg.senderName || 'Participante'}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {formatDate(msg.createdAt)}
                        </Text>
                        <Badge size="xs" variant="light" color={msg.messageType === 'transcription' ? 'blue' : 'gray'}>
                          {msg.messageType === 'transcription' ? 'Transcrição' : 'Chat'}
                        </Badge>
                      </Group>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                        {msg.text}
                      </Text>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Text c="dimmed" fs="italic" ta="center">
                  Nenhuma mensagem na conversa
                </Text>
              )}
            </ScrollArea>
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Edit Field Modal */}
      <Modal
        opened={editModalOpened}
        onClose={closeEditModal}
        title={
          <Group gap="xs">
            <IconEdit size={20} />
            <Text fw={600}>Editar Campo</Text>
          </Group>
        }
        centered
        size="md"
      >
        <Stack gap="md">
          {renderFieldInput()}

          {editingField?.llmPromptHint && (
            <Text size="xs" c="dimmed" fs="italic">
              Dica: {editingField.llmPromptHint}
            </Text>
          )}

          <Divider />

          <Group justify="space-between">
            <Button
              variant="subtle"
              color="red"
              onClick={handleClearField}
              loading={saving}
              disabled={!editingField?.value}
            >
              Limpar campo
            </Button>

            <Group gap="sm">
              <Button variant="default" onClick={closeEditModal}>
                Cancelar
              </Button>
              <Button
                onClick={handleSaveField}
                loading={saving}
                leftSection={<IconCheck size={16} />}
              >
                Salvar
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};
